import { Link } from 'react-router-dom'
import { useDocumentMeta } from '../hooks/useDocumentMeta'

export default function Prices() {
  useDocumentMeta({
    title: 'Priser | Psykoterapi, Parterapi & Familieterapi i Svendborg',
    description: 'Se priser for psykoterapi, parterapi i Svendborg og familieterapi på Fyn hos Birgitte Vilsgaard. Nem adgang til klinikken på Dronningemaen 68.',
    image: 'https://assets.auxodev.dk/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg'
  })
  const priceItems = [
    {
      title: 'Individuel terapi',
      price: '850 kr.',
      duration: '60 minutter',
      description: 'Individuel samtale med fuldt fokus på dig, dine udfordringer, mønstre og personlige udvikling.',
      icon: 'fa-user'
    },
    {
      title: 'Parterapi',
      price: '1.200 kr.',
      duration: '90 minutter',
      description: 'Fælles session for dig og din partner, hvor vi arbejder konstruktivt med jeres relation og konflikter.',
      icon: 'fa-users'
    },
    {
      title: 'Walk-and-Talk',
      price: '850 kr.',
      duration: '60 minutter',
      description: 'Samtaleterapi i den sydfynske natur nær skov og strand i Svendborg. En rolig og uformel terapiform.',
      icon: 'fa-tree'
    },
    {
      title: 'Online terapi',
      price: '850 kr.',
      duration: '60 minutter',
      description: 'Terapi via Teams, Skype eller Whereby. Fleksibelt og trygt direkte fra dit eget hjem.',
      icon: 'fa-laptop'
    }
  ]

  return (
    <div className="pt-[72px] min-h-screen bg-brand-offwhite">
      {/* Hero Section */}
      <section className="relative bg-brand-dark text-brand-offwhite py-20 lg:py-28 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg"
            alt="Priser & Praktisk info - Terapien.dk"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/85 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col gap-4">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-sage">
            <i className="fa-solid fa-tag text-[10px]" /> Priser & Information
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-sand leading-tight">
            Priser & praktisk
          </h1>
          <p className="text-base sm:text-lg font-serif italic text-brand-offwhite/85 max-w-3xl leading-relaxed">
            Klare priser, varighed, adresse, parkering, afbudsregler og betalingsmuligheder – alt samlet ét sted, så du nemt kan orientere dig.
          </p>
        </div>
      </section>

      {/* Price Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-brand-sage font-bold">Terapi & Samtaler</span>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark mt-1">Private klientsamtaler</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {priceItems.map((item, idx) => (
              <div
                key={idx}
                className="bg-white border border-brand-sand/30 rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-300 flex flex-col gap-6"
              >
                <div className="h-12 w-12 rounded-2xl bg-brand-sand/40 flex items-center justify-center text-brand-dark text-xl">
                  <i className={`fa-light ${item.icon}`} />
                </div>
                <div className="flex flex-col gap-1">
                  <h2 className="font-serif text-xl font-bold text-brand-dark">{item.title}</h2>
                  <span className="text-xs text-brand-sage uppercase tracking-wider font-medium">{item.duration}</span>
                </div>
                <div className="flex items-baseline gap-1 border-b border-brand-sand/30 pb-4">
                  <span className="text-3xl font-bold text-brand-dark font-serif">{item.price}</span>
                  <span className="text-xs text-brand-gray/50">inkl. moms</span>
                </div>
                <p className="text-sm text-brand-gray/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {/* Myndighedsopgaver Price Highlight */}
          <div className="mt-16 bg-white border-2 border-brand-sage/40 rounded-3xl p-8 sm:p-10 shadow-lg flex flex-col lg:flex-row items-center justify-between gap-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-brand-sage text-white text-[11px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-bl-2xl">
              Kommuner & Forvaltninger
            </div>
            <div className="flex flex-col gap-3 max-w-2xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-brand-sage/15 text-brand-dark flex items-center justify-center text-xl shrink-0">
                  <i className="fa-solid fa-landmark" />
                </div>
                <div>
                  <span className="text-xs uppercase tracking-wider text-brand-sage font-bold block">Specialiseret indsats</span>
                  <h3 className="font-serif text-2xl sm:text-3xl font-bold text-brand-dark">Myndighedsopgaver</h3>
                </div>
              </div>
              <p className="text-sm sm:text-base text-brand-gray/80 leading-relaxed mt-1">
                Løsning af kommunale indsatser inden for <strong>familiebehandling</strong>, <strong>samværskonsulent</strong> (støttet og overvåget samvær), <strong>støttekontaktperson</strong> samt <strong>komplekse familieindsatser</strong>.
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                <span className="inline-flex items-center gap-1.5 bg-brand-sand/20 text-brand-dark text-xs px-3 py-1 rounded-full font-medium">
                  <i className="fa-solid fa-check text-brand-sage text-[10px]" /> Familiebehandling
                </span>
                <span className="inline-flex items-center gap-1.5 bg-brand-sand/20 text-brand-dark text-xs px-3 py-1 rounded-full font-medium">
                  <i className="fa-solid fa-check text-brand-sage text-[10px]" /> Samværskonsulent
                </span>
                <span className="inline-flex items-center gap-1.5 bg-brand-sand/20 text-brand-dark text-xs px-3 py-1 rounded-full font-medium">
                  <i className="fa-solid fa-check text-brand-sage text-[10px]" /> Støttekontaktperson
                </span>
                <span className="inline-flex items-center gap-1.5 bg-brand-sand/20 text-brand-dark text-xs px-3 py-1 rounded-full font-medium">
                  <i className="fa-solid fa-check text-brand-sage text-[10px]" /> Komplekse familieindsatser
                </span>
              </div>
            </div>

            <div className="flex flex-col items-center sm:items-end text-center sm:text-right shrink-0 gap-3 border-t lg:border-t-0 lg:border-l border-brand-sand/40 pt-6 lg:pt-0 lg:pl-10 w-full lg:w-auto">
              <div className="flex flex-col items-center sm:items-end">
                <span className="text-4xl font-bold font-serif text-brand-dark">750 kr.</span>
                <span className="text-xs text-brand-sage font-semibold uppercase tracking-wider mt-1">
                  pr. time / opgave
                </span>
                <span className="text-xs text-brand-gray/70 mt-1 max-w-[200px] leading-tight">
                  + tillæg ved aften, weekender &amp; helligdage
                </span>
              </div>
              <Link
                to="/kontakt"
                className="mt-2 bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-xs font-semibold uppercase tracking-wider px-6 py-3 rounded-full transition-all duration-200 hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"
              >
                Kontakt mig
              </Link>
            </div>
          </div>

          {/* Download PDF Flyer Card */}
          <div className="mt-8 bg-brand-sand/20 border border-brand-sand/40 rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white text-red-600 shadow-sm flex items-center justify-center text-2xl shrink-0">
                <i className="fa-solid fa-file-pdf" />
              </div>
              <div>
                <h4 className="font-serif text-lg font-bold text-brand-dark">
                  Terapien.dk Informationsmateriale (A5 Flyer)
                </h4>
                <p className="text-xs sm:text-sm text-brand-gray/70 mt-0.5">
                  "Du behøver ikke være stærk hele tiden" – Samtaleterapi for unge, voksne og pårørende samt faglige indsatser.
                </p>
              </div>
            </div>
            <a
              href="/_assets/terapien-helside-a5-42.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-xs font-semibold uppercase tracking-wider px-6 py-3.5 rounded-full transition-all duration-200 shrink-0 shadow-sm hover:shadow"
            >
              <i className="fa-solid fa-download text-xs" />
              <span>Åbn flyer (PDF)</span>
            </a>
          </div>
        </div>
      </section>

      {/* Practical Information */}
      <section className="py-20 bg-brand-sand/10 border-t border-b border-brand-sand/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-xs uppercase tracking-[0.2em] text-brand-sage font-bold">Trygge & gennemskuelige rammer</span>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark mt-1">Praktisk information</h2>
            <p className="text-sm sm:text-base text-brand-gray/75 max-w-2xl mx-auto mt-2 leading-relaxed">
              Her finder du alle praktiske oplysninger om klinikadresse, parkering, online muligheder, betaling, afbud og tilskud.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* 1. Adresse */}
            <div className="bg-white border border-brand-sand/30 rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-300 flex flex-col gap-4">
              <div className="h-12 w-12 rounded-2xl bg-brand-sand/40 flex items-center justify-center text-brand-dark text-lg">
                <i className="fa-light fa-location-dot" />
              </div>
              <h3 className="font-serif text-xl font-bold text-brand-dark">Adresse & klinik</h3>
              <p className="text-sm text-brand-gray/70 leading-relaxed">
                Terapien finder sted i rolige og uforstyrrede rammer centralt i Svendborg – nem adgang for hele Fyn:
              </p>
              <div className="bg-brand-sand/15 rounded-2xl p-4 border border-brand-sand/30 text-sm">
                <p className="font-bold text-brand-dark">Terapien.dk v/ Birgitte Vilsgaard</p>
                <p className="text-brand-gray/80 mt-0.5">Dronningemaen 68</p>
                <p className="text-brand-gray/80">5700 Svendborg</p>
              </div>
              <a
                href="https://maps.google.com/?q=Dronningemaen+68,+5700+Svendborg"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-semibold text-brand-sage hover:text-brand-dark uppercase tracking-wider mt-auto pt-2"
              >
                <span>Find vej på Google Maps</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
              </a>
            </div>

            {/* 2. Parkering & Ankomst */}
            <div className="bg-white border border-brand-sand/30 rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-300 flex flex-col gap-4">
              <div className="h-12 w-12 rounded-2xl bg-brand-sand/40 flex items-center justify-center text-brand-dark text-lg">
                <i className="fa-light fa-square-parking" />
              </div>
              <h3 className="font-serif text-xl font-bold text-brand-dark">Parkering</h3>
              <p className="text-sm text-brand-gray/70 leading-relaxed">
                Der er gode og nemme parkeringsmuligheder direkte ved eller tæt på klinikken på Dronningemaen i Svendborg.
              </p>
              <ul className="text-sm text-brand-gray/70 space-y-2 mt-1 p-0 m-0">
                <li className="flex items-start gap-1">
                  <i className="fa-solid fa-check text-brand-sage text-xs mt-1 shrink-0" />
                  <span>Gratis parkering i nærområdet.</span>
                </li>
                <li className="flex items-start gap-1">
                  <i className="fa-solid fa-check text-brand-sage text-xs mt-1 shrink-0" />
                  <span>Nem adgang med offentlig transport og kort gåafstand fra Svendborg Station.</span>
                </li>
                <li className="flex items-start gap-1">
                  <i className="fa-solid fa-check text-brand-sage text-xs mt-1 shrink-0" />
                  <span>Diskret og rolig indgang uden travlt venteværelse.</span>
                </li>
              </ul>
            </div>

            {/* 3. Mulighed for online samtale */}
            <div className="bg-white border border-brand-sand/30 rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-300 flex flex-col gap-4">
              <div className="h-12 w-12 rounded-2xl bg-brand-sand/40 flex items-center justify-center text-brand-dark text-lg">
                <i className="fa-light fa-video" />
              </div>
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-xl font-bold text-brand-dark">Online samtaler</h3>
                <span className="text-[10px] uppercase tracking-wider bg-brand-sage/20 text-brand-dark font-bold px-2.5 py-1 rounded-full">
                  Tilbydes
                </span>
              </div>
              <p className="text-sm text-brand-gray/70 leading-relaxed">
                Hvis du bor længere væk, har en travl hverdag, er udfordret på helbredet eller foretrækker at sidde i dine egne vante rammer, tilbydes individuel terapi også online.
              </p>
              <ul className="text-sm text-brand-gray/70 space-y-2 mt-1 p-0 m-0">
                <li className="flex items-start gap-1">
                  <i className="fa-solid fa-check text-brand-sage text-xs mt-1 shrink-0" />
                  <span>Afholdes via sikker videolink (Teams, Skype eller Whereby).</span>
                </li>
                <li className="flex items-start gap-1">
                  <i className="fa-solid fa-check text-brand-sage text-xs mt-1 shrink-0" />
                  <span>Kræver ingen kompliceret installation for dig.</span>
                </li>
                <li className="flex items-start gap-1">
                  <i className="fa-solid fa-check text-brand-sage text-xs mt-1 shrink-0" />
                  <span>Samme varighed (60 min) og pris som fremmødesamtaler.</span>
                </li>
              </ul>
            </div>

            {/* 4. Betalingsmuligheder */}
            <div className="bg-white border border-brand-sand/30 rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-300 flex flex-col gap-4">
              <div className="h-12 w-12 rounded-2xl bg-brand-sand/40 flex items-center justify-center text-brand-dark text-lg">
                <i className="fa-light fa-credit-card" />
              </div>
              <h3 className="font-serif text-xl font-bold text-brand-dark">Betalingsmuligheder</h3>
              <p className="text-sm text-brand-gray/70 leading-relaxed">
                Afregning sker umiddelbart i forlængelse af hver session.
              </p>
              <div className="space-y-2 text-sm text-brand-gray/70">
                <div className="flex items-center gap-3 p-3 bg-brand-sand/15 rounded-xl border border-brand-sand/30">
                  <i className="fa-solid fa-mobile-screen text-brand-sage text-lg" />
                  <span className="text-brand-dark font-medium">MobilePay</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-brand-sand/15 rounded-xl border border-brand-sand/30">
                  <i className="fa-solid fa-money-bill-wave text-brand-sage text-lg" />
                  <span className="text-brand-dark font-medium">Kontant betaling</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-brand-sand/15 rounded-xl border border-brand-sand/30">
                  <i className="fa-solid fa-building-columns text-brand-sage text-lg" />
                  <span className="text-brand-dark font-medium">Bankoverførsel (online samtaler)</span>
                </div>
              </div>
              <p className="text-xs text-brand-gray/60 italic mt-auto">
                * Bemærk: Der modtages ikke dankort/betalingskort i klinikken.
              </p>
            </div>

            {/* 5. Afbudsregler */}
            <div className="bg-white border border-brand-sand/30 rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-300 flex flex-col gap-4">
              <div className="h-12 w-12 rounded-2xl bg-brand-sand/40 flex items-center justify-center text-brand-dark text-lg">
                <i className="fa-light fa-calendar-xmark" />
              </div>
              <h3 className="font-serif text-xl font-bold text-brand-dark">Afbudsregler</h3>
              <p className="text-sm text-brand-gray/70 leading-relaxed">
                Bliver du forhindret i at møde op, bedes du melde afbud hurtigst muligt, så tiden eventuelt kan tilbydes til en anden.
              </p>
              <div className="bg-amber-50/70 border border-amber-200/60 rounded-2xl p-4 text-xs sm:text-sm text-brand-dark space-y-1.5">
                <p className="font-bold flex items-center gap-1.5">
                  <i className="fa-solid fa-clock text-amber-700" />
                  Senest 24 timer før
                </p>
                <p className="text-brand-gray/80 leading-relaxed">
                  Afbud skal meddeles på telefon/sms (<a href="tel:28839488" className="underline font-semibold hover:text-brand-dark">28 83 94 88</a>) eller mail senest 24 timer forud.
                </p>
                <p className="text-brand-gray/70 text-xs pt-1">
                  Ved senere afbud eller udeblivelse opkræves det fulde honorar, da tiden er reserveret til dig.
                </p>
              </div>
            </div>

            {/* 6. Tilskud & Forsikring */}
            <div className="bg-white border border-brand-sand/30 rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-300 flex flex-col gap-4">
              <div className="h-12 w-12 rounded-2xl bg-brand-sand/40 flex items-center justify-center text-brand-dark text-lg">
                <i className="fa-light fa-shield-halved" />
              </div>
              <h3 className="font-serif text-xl font-bold text-brand-dark">Tilskud & Forsikring</h3>
              <p className="text-sm text-brand-gray/70 leading-relaxed">
                Som Registreret Psykoterapeut (Reg. Psykoterapeut FaDP) gælder følgende:
              </p>
              <ul className="text-sm text-brand-gray/70 space-y-2 p-0 m-0 list-none">
                <li className="flex items-start gap-1 pl-0 ml-0">
                  <i className="fa-solid fa-check text-brand-sage text-xs mt-1 shrink-0" />
                  <span>Der ydes ikke tilskud via den offentlige sygesikring eller Sygeforsikringen "danmark".</span>
                </li>
                <li className="flex items-start gap-1 pl-0 ml-0">
                  <i className="fa-solid fa-check text-brand-sage text-xs mt-1 shrink-0" />
                  <span>Mange private sundhedsforsikringer og arbejdsgiverbetalte ordninger dækker helt eller delvist. Undersøg dine vilkår hos dit forsikringsselskab.</span>
                </li>
              </ul>
              <p className="text-xs text-brand-gray/60 italic mt-auto">
                Forhør dig altid direkte hos dit forsikringsselskab om dine dækningsmuligheder for psykoterapi.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Contact CTA */}
      <section className="py-20 bg-brand-offwhite text-center">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-6">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark">
            Har du spørgsmål til priser eller forløb?
          </h2>
          <p className="text-sm sm:text-base text-brand-gray/70 max-w-xl leading-relaxed">
            Du er altid velkommen til at kontakte mig for en uforpligtende afklarende samtale, hvor vi kan drøfte dine behov.
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
            >
              Book en samtale
            </Link>
            <Link
              to="/kontakt"
              className="bg-brand-offwhite border border-brand-dark/30 hover:border-brand-dark text-brand-dark text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-200 hover:bg-white hover:scale-[1.02] active:scale-[0.98]"
            >
              Kontakt mig
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
